// ============================================================================
// Blackout Utility
// Shared rules for site and store blackout periods.
// Pure functions only — no React, no services — so both the config screens and
// the reservation schedule can import them, and so they stay unit-testable.
// ============================================================================

import { BlackoutType } from '../types/site';
import { formatDate, getDayOfWeek, isFuture } from './dateUtils';

/**
 * The shape every blackout rule needs. Declared structurally rather than as
 * SiteBlackout | StoreBlackout so both satisfy it without a union.
 */
export interface BlackoutLike {
  active: boolean;
  start_date: string;
  end_date: string;
}

/**
 * Is this blackout blocking bookings right now?
 *
 * end_date is EXCLUSIVE: the backend blocks nights [start_date, end_date) via
 * `start_date < check_out AND end_date > check_in`, so a blackout stops blocking
 * on the end_date itself. Keep this in step with
 * hcp-backend/app/services/availability_service.py:check_site_blackout.
 */
export function isBlackoutCurrentlyActive(blackout: BlackoutLike): boolean {
  if (!blackout.active) return false;
  return !isFuture(blackout.start_date) && isFuture(blackout.end_date);
}

/**
 * e.g. "Sat, Sep 19, 2026" — blackout dates are calendar days (YYYY-MM-DD),
 * so they go through dateUtils rather than a bare new Date().
 */
export function formatBlackoutDate(dateStr: string): string {
  return `${getDayOfWeek(dateStr)}, ${formatDate(dateStr)}`;
}

export function getBlackoutTypeIcon(type: BlackoutType): string {
  switch (type) {
    case 'maintenance': return '🔧';
    case 'seasonal': return '🍂';
    case 'emergency': return '🚨';
    case 'weather': return '🌧️';
    default: return '📅';
  }
}

/**
 * Validate the editable fields of a blackout before sending it.
 * Returns an error message, or null when the draft is good to send.
 */
export function validateBlackoutDraft(draft: {
  title: string;
  start_date: string;
  end_date: string;
}): string | null {
  if (!draft.title.trim()) {
    return 'Title is required';
  }
  if (!draft.start_date || !draft.end_date) {
    return 'Start and end dates are required';
  }
  // Dates are YYYY-MM-DD, so a string compare is a date compare. The end date
  // is exclusive and the backend rejects start >= end, so equal dates (a
  // zero-night blackout) are invalid here too.
  if (draft.end_date <= draft.start_date) {
    return 'End date must be after start date — it is the day the site becomes bookable again';
  }
  return null;
}
